import AuraType1 from "@/assets/img/aura/aura_type_1.webp"
import AuraType2 from "@/assets/img/aura/aura_type_2.webp"
import AuraType3 from "@/assets/img/aura/aura_type_3.webp"
import WomanAuraImage1 from "@/assets/img/aura/woman_aura_image_1.webp"
import WomanAuraImage2 from "@/assets/img/aura/woman_aura_image_2.webp"
import PalmIntro from "@/assets/img/aura/palm-intro.webp"
import { ANALYTICS_EVENTS } from "@/features/analytics"
import { isFilled, type QuizAnswerValue } from "../components/steps/step-contract"
import { AuraArchetypeStep } from "../components/steps/aura-archetype-step"
import { AuraCoverStep } from "../components/steps/aura-cover-step"
import { AuraEmailStep } from "../components/steps/aura-email-step"
import { AuraFeaturesStep } from "../components/steps/aura-features-step"
import { AuraFrequencyStep } from "../components/steps/aura-frequency-step"
import { AuraImageGridStep } from "../components/steps/aura-image-grid-step"
import { AuraInsightStep } from "../components/steps/aura-insight-step"
import { AuraMultiChoiceStep } from "../components/steps/aura-multi-choice-step"
import { AuraPalmIntroStep } from "../components/steps/aura-palm-intro-step"
import { AuraProcessingStep } from "../components/steps/aura-processing-step"
import { AuraRadarStep } from "../components/steps/aura-radar-step"
import { AuraSocialProofStep } from "../components/steps/aura-social-proof-step"
import { PalmCaptureStep } from "../components/steps/palm-capture-step"
import { SoulmateChoiceStep } from "../components/steps/soulmate-choice-step"
import type { QuizAnswers, QuizConfig, QuizOption } from "../types"
import { ageFromDob } from "./event-params"

const requiredValidate = (value: QuizAnswerValue | undefined, step: { required?: boolean }) =>
  step.required ? isFilled(value) : true

const has = (answers: QuizAnswers, key: string, value: string) => answers[key] === value

const oneOf = (answers: QuizAnswers, key: string, values: string[]) =>
  values.includes(typeof answers[key] === "string" ? (answers[key] as string) : "")

const emoji = (icon: string, label: string, value: string, description?: string): QuizOption => ({
  icon,
  label,
  value,
  ...(description ? { description } : {}),
})

/**
 * Aura Scan quiz (Figma Live-Palmistry-4.0 Quiz section 6647:36411). Cover →
 * social proof → 25 counted questions (gender through left-palm capture) with
 * answer-gated interstitials. Completion routes to the `aura-scan` checkout.
 */
export const auraScanQuiz: QuizConfig = {
  id: "aura-scan",
  name: "Aura Scan",
  steps: [
    {
      id: "cover",
      eventOnShow: ANALYTICS_EVENTS.AURA_QUIZ_LOADED,
      type: "cover",
      title: "Discover Is Your Aura Look Like",
      subtitle:
        "Answer a few questions to reveal the colors and qualities associated with your aura",
      features: [
        { icon: "clock", label: "3-minute quiz" },
        { icon: "sparkles", label: "Reveal your energy" },
        { icon: "search", label: "Personalized insights" },
      ],
      cta: "Let’s begin",
      countsTowardProgress: false,
      component: AuraCoverStep,
    },
    {
      id: "social-proof",
      event: ANALYTICS_EVENTS.AURA_QUIZ_USERS_TRUST,
      type: "info",
      title: "Join 14+ million people who have explored their unique energy with us",
      cta: "Continue",
      countsTowardProgress: false,
      component: AuraSocialProofStep,
    },
    {
      id: "gender",
      event: ANALYTICS_EVENTS.AURA_QUIZ_1GENDER,
      eventParams: (a) => ({ gender: a.gender }),
      type: "single-choice",
      layout: "icons",
      bottomDecoration: true,
      title: "What’s your gender?",
      subtitle: "This helps us tune your aura reading to your unique energy",
      required: true,
      options: [
        { label: "Male", value: "male", icon: "mars" },
        { label: "Female", value: "female", icon: "venus" },
        { label: "Other", value: "other", icon: "ellipsis" },
      ],
    },
    {
      id: "explore-first",
      event: ANALYTICS_EVENTS.AURA_QUIZ_2EXPLORE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Which part of your aura would you like to explore first?",
      subtitle: "Choose what you’re most curious to discover",
      required: true,
      options: [
        emoji("🎨", "My aura color", "aura_color"),
        emoji("🌀", "My aura shape", "aura_shape"),
        emoji("🔥", "My aura strength", "aura_strength"),
        emoji("🔮", "My aura type", "aura_type"),
        emoji("🦋", "My spirit guide", "spirit_guide"),
      ],
    },
    {
      id: "features",
      event: ANALYTICS_EVENTS.AURA_QUIZ_FEATURES,
      type: "info",
      title: "Your aura reveals the energy that shapes you",
      countsTowardProgress: false,
      component: AuraFeaturesStep,
    },
    {
      id: "dob",
      event: ANALYTICS_EVENTS.AURA_QUIZ_3BIRTHDAY,
      eventParams: (a) => ({ age: ageFromDob(a) }),
      type: "date",
      dateMode: "dob",
      title: "What’s your date of birth?",
      subtitle: "Your birth date helps us identify your astrological pattern",
      required: true,
    },
    {
      id: "tob",
      event: ANALYTICS_EVENTS.AURA_QUIZ_4BIRTHTIME,
      eventParams: (a) => ({ select: a.tob === "unknown" ? "skipped" : "entered" }),
      type: "time",
      title: "Do you know your birth time?",
      subtitle: "Your birth time makes your chart more precise",
      required: true,
      secondaryAction: { label: "I don’t remember", value: "unknown" },
    },
    {
      id: "birthplace",
      event: ANALYTICS_EVENTS.AURA_QUIZ_5BIRTHPLACE,
      type: "input-city",
      title: "Where were you born?",
      subtitle: "Your birthplace helps calculate the positions in your birth chart",
      placeholder: "Place of birth",
      required: true,
      reassurance: "We don't share any personal information",
    },
    {
      id: "atmosphere",
      event: ANALYTICS_EVENTS.AURA_QUIZ_6ATMOSPHERE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "How quickly do you notice a change in the atmosphere around you?",
      subtitle: "Some shifts are easier to sense than see",
      required: true,
      options: [
        emoji("⚡", "Almost immediately", "almost_immediately"),
        emoji("⏳", "After a little while", "after_a_little_while"),
        emoji("☁️", "Rarely", "rarely"),
        emoji("🤔", "I’m not sure", "im_not_sure"),
      ],
    },
    {
      id: "animals",
      event: ANALYTICS_EVENTS.AURA_QUIZ_7ANIMALS,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "How do animals tend to respond when they first meet you?",
      subtitle: "Some connections happen without words",
      required: true,
      options: [
        emoji("🐈", "They approach me quickly", "approach_quickly"),
        emoji("👀", "They observe me before coming closer", "observe_before_closer"),
        emoji("🦔", "They usually keep their distance", "keep_distance"),
        emoji("🔄", "It varies a lot", "varies_a_lot"),
      ],
    },
    {
      id: "presence",
      event: ANALYTICS_EVENTS.AURA_QUIZ_8PRESENCE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What do people most often seem to feel around you?",
      subtitle: "Your presence can leave a subtle impression",
      required: true,
      options: [
        emoji("☀️", "Uplifted and energized", "uplifted_energized"),
        emoji("🕊️", "Calm and at ease", "calm_ease"),
        emoji("🌊", "Emotionally overwhelmed", "emotionally_overwhelmed"),
        emoji("🎭", "It depends on the person", "depends_on_person"),
        emoji("💭", "I’m not sure", "not_sure"),
      ],
    },
    {
      id: "archetype-radiant",
      event: ANALYTICS_EVENTS.AURA_QUIZ_ARCHETYPE,
      type: "info",
      title: "The Radiant Catalyst",
      subtitle: "Your presence naturally brings warmth and momentum into a room",
      coverImage: AuraType1,
      countsTowardProgress: false,
      showIf: (a) => has(a, "presence", "uplifted_energized"),
      component: AuraArchetypeStep,
    },
    {
      id: "archetype-grounded",
      event: ANALYTICS_EVENTS.AURA_QUIZ_ARCHETYPE,
      type: "info",
      title: "The Grounded Anchor",
      subtitle: "Your calm presence helps others feel safe, steady, and deeply understood",
      coverImage: AuraType2,
      countsTowardProgress: false,
      showIf: (a) => oneOf(a, "presence", ["calm_ease", "not_sure"]),
      component: AuraArchetypeStep,
    },
    {
      id: "archetype-empath",
      event: ANALYTICS_EVENTS.AURA_QUIZ_ARCHETYPE,
      type: "info",
      title: "The Intuitive Empath",
      subtitle: "You notice subtle emotional shifts before they are ever put into words",
      coverImage: AuraType3,
      countsTowardProgress: false,
      showIf: (a) => oneOf(a, "presence", ["emotionally_overwhelmed", "depends_on_person"]),
      component: AuraArchetypeStep,
    },
    {
      id: "open-up",
      event: ANALYTICS_EVENTS.AURA_QUIZ_9OPEN,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "When meeting new people, do you usually open up slowly or quickly?",
      subtitle: "Every connection begins at its own pace",
      required: true,
      options: [
        emoji("🤝", "I connect easily", "connect_easily"),
        emoji("🌱", "I warm up gradually", "warm_up_gradually"),
        emoji("👁️", "I prefer to observe first", "observe_first"),
        emoji("🧭", "It depends on the setting", "depends_on_setting"),
      ],
    },
    {
      id: "crowd",
      event: ANALYTICS_EVENTS.AURA_QUIZ_10CROWD,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "How does spending time in a crowd usually affect you?",
      subtitle: "Different spaces can shift your energy",
      required: true,
      options: [
        emoji("🔋", "It energizes me", "energizes"),
        emoji("⏳", "I feel fine at first, then get tired", "tired_later"),
        emoji("🪫", "It drains me quickly", "drains_quickly"),
        emoji("👥", "It depends on the people", "depends_on_people"),
      ],
    },
    {
      id: "struggling",
      event: ANALYTICS_EVENTS.AURA_QUIZ_11STRUGGLING,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "When someone is struggling, what do you naturally do?",
      subtitle: "Notice how you naturally show up for others",
      required: true,
      options: [
        emoji("🤗", "Offer emotional support", "emotional_support"),
        emoji("🤫", "Listen without judging", "listen_without_judging"),
        emoji("💡", "Suggest a practical solution", "practical_solution"),
        emoji("🚪", "Give them space", "give_space"),
      ],
    },
    {
      id: "tension",
      event: ANALYTICS_EVENTS.AURA_QUIZ_12TENSION,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "When tension arises, what response comes most naturally to you?",
      subtitle: "Your instincts can shape how you navigate tension",
      required: true,
      options: [
        emoji("💬", "I express my point of view", "express_point_of_view"),
        emoji("👂", "I listen before responding", "listen_before_responding"),
        emoji("🤝", "I look for common ground", "common_ground"),
        emoji("⚖️", "It depends on the situation", "depends_on_situation"),
      ],
    },
    {
      id: "purpose",
      event: ANALYTICS_EVENTS.AURA_QUIZ_13PURPOSE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Which kind of purpose feels most meaningful to you?",
      subtitle: "Meaning can take many different forms",
      required: true,
      options: [
        emoji("🌱", "Creating positive change", "creating_positive_change"),
        emoji("🫶", "Supporting other people", "supporting_other_people"),
        emoji("📚", "Learning and growing", "learning_and_growing"),
        emoji("🌈", "Bringing more joy into life", "bringing_more_joy"),
      ],
    },
    {
      id: "element",
      event: ANALYTICS_EVENTS.AURA_QUIZ_14ELEMENT,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Which element do you feel most connected to?",
      subtitle: "Choose the element whose qualities feel closest to your own",
      required: true,
      options: [
        { label: "Fire", value: "fire", icon: "fire", description: "Passionate and driven" },
        { label: "Water", value: "water", icon: "water", description: "Emotional and intuitive" },
        { label: "Earth", value: "earth", icon: "earth", description: "Grounded and practical" },
        { label: "Air", value: "air", icon: "air", description: "Intellectual and free-spirited" },
      ],
    },
    {
      id: "sense-presence",
      event: ANALYTICS_EVENTS.AURA_QUIZ_15SENSE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "How easily do you sense someone’s presence or energy?",
      subtitle: "Some presences are felt before they are noticed",
      required: true,
      options: [
        emoji("⚡", "Almost immediately", "almost_immediately"),
        emoji("🌙", "Sometimes", "sometimes"),
        emoji("💭", "I’m not sure", "im_not_sure"),
        emoji("💤", "Rarely", "rarely"),
      ],
    },
    {
      id: "intuition-clearly",
      event: ANALYTICS_EVENTS.AURA_QUIZ_INTUITION,
      type: "info",
      title: "YOUR INTUITION SPEAKS CLEARLY",
      subtitle: "You naturally pick up on signals others may miss",
      coverImage: WomanAuraImage1,
      countsTowardProgress: false,
      showIf: (a) => oneOf(a, "sense-presence", ["almost_immediately", "sometimes"]),
      component: AuraInsightStep,
    },
    {
      id: "intuition-softly",
      event: ANALYTICS_EVENTS.AURA_QUIZ_INTUITION,
      type: "info",
      title: "YOUR INTUITION SPEAKS SOFTLY",
      subtitle: "Your signals tend to appear in quieter, subtler ways",
      coverImage: WomanAuraImage2,
      countsTowardProgress: false,
      showIf: (a) => oneOf(a, "sense-presence", ["im_not_sure", "rarely"]),
      component: AuraInsightStep,
    },
    {
      id: "nature-freq",
      event: ANALYTICS_EVENTS.AURA_QUIZ_16NATURE,
      type: "single-choice",
      title: "How often do you seek out nature when you need to recharge?",
      required: true,
      options: [
        emoji(
          "🌑",
          "Never",
          "never",
          "You may recharge more easily through people, movement, or familiar spaces.",
        ),
        emoji(
          "🌓",
          "Sometimes",
          "sometimes",
          "Time outdoors may help you slow down, clear your mind, and regain balance.",
        ),
        emoji(
          "🌕",
          "Often",
          "often",
          "Time outdoors appears to be one of your strongest ways to feel restored and centered.",
        ),
      ],
      component: AuraFrequencyStep,
    },
    {
      id: "weather-freq",
      event: ANALYTICS_EVENTS.AURA_QUIZ_17WEATHER,
      type: "single-choice",
      title: "How often does the weather noticeably affect your mood or energy?",
      required: true,
      options: [
        emoji(
          "🌑",
          "Never",
          "never",
          "Your mood and energy tend to remain steady as the weather changes.",
        ),
        emoji(
          "🌓",
          "Sometimes",
          "sometimes",
          "Certain skies or seasons may influence how calm, focused, or energized you feel.",
        ),
        emoji(
          "🌕",
          "Often",
          "often",
          "Changes in the weather may strongly shape your mood or energy throughout the day.",
        ),
      ],
      component: AuraFrequencyStep,
    },
    {
      id: "signs-freq",
      event: ANALYTICS_EVENTS.AURA_QUIZ_18SIGNS,
      type: "single-choice",
      title: "How often do meaningful signs or coincidences catch your attention?",
      required: true,
      options: [
        emoji(
          "🌑",
          "Never",
          "never",
          "You may prefer direct information over interpreting coincidences or symbolic patterns.",
        ),
        emoji(
          "🌓",
          "Sometimes",
          "sometimes",
          "Meaningful coincidences may stand out when they reflect what’s already on your mind.",
        ),
        emoji(
          "🌕",
          "Often",
          "often",
          "Repeated numbers, timely encounters, and familiar symbols may feel meaningful to you.",
        ),
      ],
      component: AuraFrequencyStep,
    },
    {
      id: "dreams-freq",
      event: ANALYTICS_EVENTS.AURA_QUIZ_19DREAMS,
      type: "single-choice",
      title: "How often do your dreams leave you with a strong feeling or message?",
      required: true,
      options: [
        emoji(
          "🌑",
          "Never",
          "never",
          "Feelings, observations, or waking impressions may carry more meaning for you than dreams.",
        ),
        emoji(
          "🌓",
          "Sometimes",
          "sometimes",
          "Certain dreams may leave an emotion or insight worth reflecting on.",
        ),
        emoji(
          "🌕",
          "Often",
          "often",
          "Dreams may help you process emotions, patterns, and unspoken thoughts.",
        ),
      ],
      component: AuraFrequencyStep,
    },
    {
      id: "radar",
      event: ANALYTICS_EVENTS.AURA_QUIZ_RADAR,
      type: "info",
      title: "Your energetic radar is becoming clearer",
      subtitle: "Here’s your sensitivity profile",
      countsTowardProgress: false,
      component: AuraRadarStep,
    },
    {
      id: "recharge-place",
      event: ANALYTICS_EVENTS.AURA_QUIZ_20RECHARGE,
      type: "single-choice",
      title: "Where do you recharge most easily?",
      required: true,
      options: [
        { label: "At home", value: "at_home" },
        { label: "In a big city", value: "in_a_big_city" },
        { label: "In the village", value: "in_the_village" },
        { label: "In a forest", value: "in_a_forest" },
        { label: "By the ocean", value: "by_the_ocean" },
        { label: "In the mountains", value: "in_the_mountains" },
      ],
      component: AuraImageGridStep,
    },
    {
      id: "activity",
      event: ANALYTICS_EVENTS.AURA_QUIZ_21ACTIVITY,
      type: "single-choice",
      title: "Which activity makes you feel most like yourself?",
      required: true,
      options: [
        { label: "Music", value: "music" },
        { label: "Dancing", value: "dancing" },
        { label: "Conversations", value: "conversations" },
        { label: "Reading", value: "reading" },
        { label: "Astrology", value: "astrology" },
        { label: "Meditating", value: "meditating" },
        { label: "Running", value: "running" },
        { label: "Journaling", value: "journaling" },
      ],
      component: AuraImageGridStep,
    },
    {
      id: "practices",
      event: ANALYTICS_EVENTS.AURA_QUIZ_22PRACTICES,
      type: "multiple-choice",
      title: "Which practices help you connect with your spiritual side?",
      subtitle: "Please select all that apply",
      required: true,
      options: [
        emoji("🃏", "Tarot", "tarot"),
        emoji("🪐", "Birth chart readings", "birth_chart"),
        emoji("🌟", "Horoscopes", "horoscopes"),
        emoji("🔢", "Numerology", "numerology"),
        emoji("✋", "Palm readings", "palm_readings"),
        emoji("🗺️", "Astrocartography", "astrocartography"),
        emoji("💎", "Crystals", "crystals"),
        emoji("🚫", "None of these", "none"),
      ],
    },
    {
      id: "phenomenon",
      event: ANALYTICS_EVENTS.AURA_QUIZ_23PHENOMENON,
      type: "single-choice",
      title: "Which phenomenon draws you in most?",
      required: true,
      options: [
        { label: "Thunderstorms", value: "thunderstorms" },
        { label: "Changing seasons", value: "changing_seasons" },
        { label: "Solar storms", value: "solar_storms" },
        { label: "Retrogrades", value: "retrogrades" },
        { label: "New Moon", value: "new_moon" },
        { label: "Full Moon", value: "full_moon" },
      ],
      component: AuraImageGridStep,
    },
    {
      id: "sensations",
      event: ANALYTICS_EVENTS.AURA_QUIZ_24SENSATIONS,
      type: "multiple-choice",
      title: "Which physical sensations do you associate with your intuition?",
      subtitle: "Please select all that apply",
      required: true,
      options: [
        emoji("✨", "Tingling", "tingling"),
        emoji("🥶", "Shiver", "shiver"),
        emoji("💗", "Racing heartbeat", "racing_heartbeat"),
        emoji("🔥", "Sudden warmth", "sudden_warmth"),
        emoji("🪶", "Goosebumps", "goosebumps"),
        emoji("💫", "Dizziness", "dizziness"),
        emoji("🪢", "Tension", "tension"),
        emoji("🚫", "None of these", "none"),
      ],
    },
    {
      id: "palm-intro",
      event: ANALYTICS_EVENTS.AURA_QUIZ_PALM_INTRO,
      type: "info",
      title: "Your Palm May Hold the Missing Piece of Your Aura",
      subtitle:
        "Your palm lines may reveal another layer of your energy and complete your aura profile",
      coverImage: PalmIntro,
      countsTowardProgress: false,
      component: AuraPalmIntroStep,
    },
    {
      id: "left-palm",
      event: ANALYTICS_EVENTS.AURA_QUIZ_PHOTO,
      type: "photo",
      title: "Take a photo of your left palm",
      component: PalmCaptureStep,
      fullBleed: true,
    },
    {
      id: "analysis",
      eventOnShow: ANALYTICS_EVENTS.AURA_QUIZ_PROCESSING,
      type: "analysis",
      title: "Revealing deeper layers of your aura",
      countsTowardProgress: false,
      checklist: [
        "Reading your unique palm patterns",
        "Connecting your palm to your energy",
        "Completing your personal aura profile",
      ],
    },
    {
      id: "email",
      event: ANALYTICS_EVENTS.AURA_QUIZ_EMAIL,
      type: "email",
      title: "Are you ready to discover the energy of your aura?",
      subtitle:
        "Enter your email to receive your personalized aura report and discover the energy surrounding you",
      placeholder: "Enter your email",
      required: true,
      legalNote: true,
      countsTowardProgress: false,
      component: AuraEmailStep,
    },
  ],
  stepOverrides: {
    "single-choice": {
      autoAdvance: true,
      validate: requiredValidate,
      Component: SoulmateChoiceStep,
    },
    "multiple-choice": {
      validate: requiredValidate,
      Component: AuraMultiChoiceStep,
    },
    analysis: {
      fullBleed: true,
      validate: () => true,
      Component: AuraProcessingStep,
    },
  },
}
